import { useEffect, useState } from "react";
import {
  requestRaffleBonusInit,
  requestRaffleById,
} from "@/apps/raffle/services/raffleApi";
import {
  RaffleInitResponse,
  RaffleParticipant,
  RaffleResults,
} from "@/apps/raffle/types";
import {
  getRaffleDataKey,
  useRaffleDataStore,
} from "@/apps/raffle/store/raffleDataStore";
import { sortParticipantsByChance } from "./utils";

export const useRaffleData = (yandexId: string | null, bonusId?: string) => {
  const cacheKey = yandexId ? getRaffleDataKey(yandexId, bonusId) : null;
  const cachedRaffleEntry = useRaffleDataStore((state) =>
    cacheKey ? state.raffleByKey[cacheKey] : undefined
  );
  const hasCachedRaffle = cachedRaffleEntry !== undefined;
  const cachedRaffle = cachedRaffleEntry ?? null;
  const cacheRaffle = useRaffleDataStore((state) => state.setRaffle);
  const [raffle, setRaffle] = useState<RaffleInitResponse | null>(() =>
    cachedRaffle
  );
  const [participants, setParticipants] = useState<RaffleParticipant[]>(() =>
    sortParticipantsByChance(cachedRaffle?.participants ?? [])
  );
  const [isLoading, setIsLoading] = useState(() => !hasCachedRaffle);
  const [isEmpty, setIsEmpty] = useState(false);
  const [results, setResults] = useState<RaffleResults | null>(
    () => cachedRaffle?.result ?? null
  );
  const [resultsSeconds, setResultsSeconds] = useState<number | null>(
    () => cachedRaffle?.result_seconds ?? null
  );

  useEffect(() => {
    if (!hasCachedRaffle) return;

    if (!cachedRaffle) {
      setRaffle(null);
      setParticipants([]);
      setResults(null);
      setResultsSeconds(null);
      setIsEmpty(!bonusId);
      setIsLoading(false);
      return;
    }

    setRaffle(cachedRaffle);
    setParticipants(sortParticipantsByChance(cachedRaffle.participants));
    setResults(cachedRaffle.result ?? null);
    setResultsSeconds(cachedRaffle.result_seconds ?? null);
    setIsEmpty(false);
    setIsLoading(false);
  }, [bonusId, cachedRaffle, hasCachedRaffle]);

  useEffect(() => {
    let isCurrentRequest = true;

    const loadRafflePage = async () => {
      if (!yandexId) {
        setRaffle(null);
        setParticipants([]);
        setResults(null);
        setResultsSeconds(null);
        setIsEmpty(false);
        setIsLoading(false);
        return;
      }

      if (hasCachedRaffle) {
        return;
      }

      setIsLoading(true);

      try {
        const bonusInitData = bonusId
          ? null
          : await requestRaffleBonusInit(yandexId);
        const raffleData = bonusId
          ? await requestRaffleById(bonusId, yandexId)
          : bonusInitData?.current_draw ?? null;

        if (!isCurrentRequest) return;

        if (!raffleData) {
          setRaffle(null);
          setParticipants([]);
          setResults(null);
          setResultsSeconds(null);
          setIsEmpty(!bonusId);
          cacheRaffle(yandexId, bonusId, null);
          return;
        }

        setRaffle(raffleData);
        setParticipants(sortParticipantsByChance(raffleData.participants));
        setResults(raffleData.result ?? null);
        setResultsSeconds(raffleData.result_seconds ?? null);
        setIsEmpty(false);
        cacheRaffle(yandexId, bonusId, raffleData);
      } catch {
        if (!isCurrentRequest) return;

        setRaffle(null);
        setParticipants([]);
        setResults(null);
        setResultsSeconds(null);
        setIsEmpty(false);
      } finally {
        if (isCurrentRequest) {
          setIsLoading(false);
        }
      }
    };

    loadRafflePage();

    return () => {
      isCurrentRequest = false;
    };
  }, [bonusId, cacheRaffle, hasCachedRaffle, yandexId]);

  return {
    raffle,
    participants,
    results,
    resultsSeconds,
    isLoading,
    isEmpty,
  };
};
